/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {Uuid} from '../../../util/uuid';
import {ApiConfig} from '../../api';
import {ResourceHelper} from '../../../util/resource-helper';
import {AdnHttpService} from '../../../util/adn-http.service';
import {
  createAdnHttpResource,
  createAdnHttpResourceAction,
  createAdnHttpResourceSender
} from '../../../util/create-adn-http-resource';
import {updateEndDateForManualInput, OrderResource} from './order.resource';
import {LocalUserProfile} from '../../../session/local-user-profile';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';
import {filterUrlLessTargeting} from '../../../util/targeting-request';

export function transformLineItemResponse(lineItem: any): any {
  if (!lineItem) {
    return lineItem;
  }
  lineItem.startDate = lineItem.startDate && new Date(lineItem.startDate);
  lineItem.endDate = lineItem.endDate && new Date(lineItem.endDate);
  if (_.isPlainObject(lineItem.bidSpecification)) {
    lineItem.bidSpecification.cpm = lineItem.bidSpecification.cpm || {};
    lineItem.bidSpecification.cpc = lineItem.bidSpecification.cpc || {};
    lineItem.bidSpecification.cpa = lineItem.bidSpecification.cpa || {};
  }
  return lineItem;
}

export function transformLineItemRequest(lineItem: any): any {
  filterUrlLessTargeting(lineItem);
  if (_.isPlainObject(lineItem.bidSpecification)) {
    _.forEach(['cpm', 'cpc'], function(k) {
      ResourceHelper.checkMoney(lineItem, ['bidSpecification', k]);
    });
    ResourceHelper.checkMoney(lineItem, ['bidSpecification', 'cpa'], 'cost');
    ResourceHelper.checkMoney(lineItem, ['bidSpecification', 'cpa'], 'initialECpm');
  }
  ResourceHelper.checkObjectives(lineItem);
  updateEndDateForManualInput(lineItem, 'endDate');
  delete lineItem.reportExecutions;
  return lineItem;
}

/** Query-string flags previously wired via ngResource `@ignoreProduct` etc. on save/approve. */
const LINE_ITEM_SAVE_QUERY_PARAMS = [
  'ignoreProduct',
  'updateOnlyTheseTargetTypes',
  'skipTeamBasedTargeting',
  'skipTeamBasedTargetingType'
];

/** Angular DI token for the LineItemTemplate HTTP resource (factory product). */
export class LineItemTemplateResource {
}

/** Angular DI token for the LineItemReview HTTP resource (factory product). */
export class LineItemReviewResource {
}

/** Angular DI token for the LineItemBookings HTTP resource (factory product). */
export class LineItemBookingsResource {
}

/** Angular DI token for the LineItem HTTP resource (factory product). */
export class LineItemResource {
}

export function createLineItemTemplateResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile
): any {
  const send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  const uri = ApiConfig.obtain().getVersionedUri('lineitemtemplates');
  const defaults = {showBudgetSpend: true};
  const LineItemTemplate = createAdnHttpResource(
    send,
    'lineitemtemplates',
    () => localUserProfile,
    'lineItemTemplate',
    defaults,
    {
      transformRequest: transformLineItemRequest,
      transformResponse: transformLineItemResponse,
      saveQueryParams: LINE_ITEM_SAVE_QUERY_PARAMS
    }
  );
  LineItemTemplate.approve = createAdnHttpResourceAction(send, {
    method: 'POST',
    url: uri + '/:id/approve',
    defaultParams: defaults,
    queryParams: ['showBudgetSpend'].concat(LINE_ITEM_SAVE_QUERY_PARAMS),
    transformRequest: transformLineItemRequest,
    transformResponse: transformLineItemResponse
  });
  LineItemTemplate.lineItem = createAdnHttpResourceAction(send, {
    method: 'POST',
    url: uri + '/:id/lineitem',
    defaultParams: defaults,
    queryParams: ['showBudgetSpend', 'lineItemId', 'orderId', 'name', 'copyTargeting', 'userState', 'startDate', 'endDate']
  });
  LineItemTemplate.copy = createAdnHttpResourceAction(send, {
    method: 'POST',
    url: uri + '/:id/copy',
    defaultParams: defaults,
    queryParams: ['showBudgetSpend', 'lineItemId', 'orderId', 'name', 'copyTargeting', 'copyCreatives', 'userState']
  });
  LineItemTemplate.create = function(defaults) {
    return new LineItemTemplate(_.merge({
      targeting: {},
      type: 'AUCTION',
      companionAds: 'NONE',
      deliveryCurve: 'EVEN',
      bidSpecification: {
        cpm: {},
        cpc: {}
      }
    }, defaults));
  };

  LineItemTemplate.copyAsLineItem = function(lineItemTemplate, newId, copyTargeting) {
    if (!lineItemTemplate || !lineItemTemplate.id) {
      return Promise.reject({data: {errors: [{error: {text: 'Insufficient parameters for the line item template copy'}}]}});
    }
    return LineItemTemplate.lineItem({
      id: lineItemTemplate.id,
      startDate: lineItemTemplate.startDate,
      endDate: lineItemTemplate.endDate,
      orderId: _.get(lineItemTemplate, ['order', 'id'], ''),
      lineItemId: newId || '',
      name: lineItemTemplate.name,
      copyTargeting: _.isBoolean(copyTargeting) ? copyTargeting : true,
      userState: lineItemTemplate.userState || ''
    });
  };

  LineItemTemplate.copyLineItemTemplate = function(lineItemTemplate, newId, copyTargeting) {
    if (!lineItemTemplate || !lineItemTemplate.id) {
      return Promise.reject({data: {errors: [{error: {text: 'Insufficient parameters for the line item template copy'}}]}});
    }
    return LineItemTemplate.copy({
      id: lineItemTemplate.id,
      orderId: _.get(lineItemTemplate, ['order', 'id'], ''),
      lineItemId: newId || '',
      name: lineItemTemplate.name,
      copyTargeting: _.isBoolean(copyTargeting) ? copyTargeting : true,
      userState: lineItemTemplate.userState || ''
    });
  };

  return LineItemTemplate;
}

export function createLineItemReviewResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile
): any {
  return createAdnHttpResource(
    createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler),
    'lineitems/review',
    () => localUserProfile,
    'lineItemReview',
    {showBudgetSpend: true}
  );
}

export function createLineItemBookingsResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile
): any {
  return createAdnHttpResource(
    createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler),
    'lineitems/bookings',
    () => localUserProfile,
    'lineItemBookings',
    {showBudgetSpend: true}
  );
}

export function createLineItemResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile,
  Order: any
): any {
  const send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  const uri = ApiConfig.obtain().getVersionedUri('lineitems');
  const defaults = {showBudgetSpend: true};
  const LineItem = createAdnHttpResource(
    send,
    'lineitems',
    () => localUserProfile,
    'lineItem',
    defaults,
    {
      transformRequest: transformLineItemRequest,
      transformResponse: transformLineItemResponse,
      saveQueryParams: LINE_ITEM_SAVE_QUERY_PARAMS
    }
  );
  LineItem.copy = createAdnHttpResourceAction(send, {
    method: 'POST',
    url: uri + '/:id/copy',
    defaultParams: defaults,
    queryParams: ['showBudgetSpend', 'lineItemId', 'orderId', 'name', 'copyTargeting', 'copyCreatives', 'copyReportSchedules', 'userState']
  });
  LineItem.dimensionsCheck = createAdnHttpResourceAction(send, {
    method: 'GET',
    url: uri + '/:id/dimensionsCheck',
    defaultParams: defaults,
    queryParams: ['showBudgetSpend', 'limitToRequestedInLastDay'],
    hasBody: false
  });
  LineItem.approve = createAdnHttpResourceAction(send, {
    method: 'POST',
    url: uri + '/:id/approve',
    defaultParams: defaults,
    queryParams: ['showBudgetSpend'].concat(LINE_ITEM_SAVE_QUERY_PARAMS),
    transformRequest: transformLineItemRequest,
    transformResponse: transformLineItemResponse
  });
  LineItem.getPublisherApprovals = createAdnHttpResourceAction(send, {
    method: 'GET',
    url: uri + '/:id/publisherapprovals',
    defaultParams: defaults,
    queryParams: ['showBudgetSpend'],
    hasBody: false
  });

  LineItem.getHydrated = function(params) {
    return LineItem.get(params).$promise.then(function(lineItem) {
      const relations = [];

      if (lineItem.order) {
        relations.push(Order.getDataView({id: lineItem.order.id}).then(function(order) {
          lineItem.order = order;
        }));
      }

      return Promise.all(relations).then(_.constant(lineItem));
    });
  };

  LineItem.create = function(defaults) {
    return new LineItem(_.merge({
      targeting: {},
      type: 'AUCTION',
      companionAds: 'NONE',
      deliveryCurve: 'EVEN',
      bidSpecification: {
        cpm: {},
        cpc: {}
      }
    }, defaults));
  };

  LineItem.dimCheck = function(id) {
    return LineItem.dimensionsCheck({id: id, limitToRequestedInLastDay: true}).$promise;
  };

  LineItem.copyLineItem = function(lineItem, newId, copyTargeting, copyCreatives, copyReportSchedules) {
    if (!lineItem || !lineItem.id) {
      return Promise.reject({data: {errors: [{error: {text: 'Insufficient parameters for the line item copy'}}]}});
    }
    return LineItem.copy({
      id: lineItem.id,
      orderId: _.get(lineItem, ['order', 'id'], ''),
      lineItemId: newId || '',
      name: lineItem.name,
      copyTargeting: _.isBoolean(copyTargeting) ? copyTargeting : true,
      copyCreatives: _.isBoolean(copyCreatives) ? copyCreatives : true,
      copyReportSchedules: _.isBoolean(copyReportSchedules) ? copyReportSchedules : true,
      userState: lineItem.userState || ''
    });
  };

  LineItem.prototype.clone = function(paramOverrides) {
    paramOverrides = paramOverrides || {};
    if (!_.has(paramOverrides, 'id')) {
      paramOverrides.id = Uuid.generate();
    }

    const newLineItem = new LineItem(_.assign({}, this, paramOverrides, {
      validationWarnings: [],
      executionState: null,
      notes: [],
      reportExecutions: []
    }));
    if (!newLineItem.bidSpecification || !newLineItem.bidSpecification.cpa || !newLineItem.bidSpecification.cpa.cost || !newLineItem.bidSpecification.cpa.cost.amount || !newLineItem.bidSpecification.cpa.initialECpm || !newLineItem.bidSpecification.cpa.initialECpm.amount || !newLineItem.bidSpecification.cpa.customEventType || !newLineItem.bidSpecification.cpa.lookBackHours) {
      newLineItem.bidSpecification = newLineItem.bidSpecification || {};
      newLineItem.bidSpecification.cpa = {};
    }
    return newLineItem;
  };

  return LineItem;
}

export const lineItemTemplateResourceProvider = {
  provide: LineItemTemplateResource,
  useFactory: createLineItemTemplateResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile]
};

export const lineItemReviewResourceProvider = {
  provide: LineItemReviewResource,
  useFactory: createLineItemReviewResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile]
};

export const lineItemBookingsResourceProvider = {
  provide: LineItemBookingsResource,
  useFactory: createLineItemBookingsResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile]
};

export const lineItemResourceProvider = {
  provide: LineItemResource,
  useFactory: createLineItemResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile, OrderResource]
};

